import { Link } from 'react-router-dom';

const lastUpdated = 'September 13, 2026';

export function EulaPage() {
  return (
    <div className="container legal-page">
      <div className="legal-page-header">
        <span className="eyebrow">End User License Agreement</span>
        <h1>End User License Agreement</h1>
        <p className="legal-page-sub">Last updated: {lastUpdated}</p>
      </div>

      <div className="legal-page-intro">
        <p>
          This End User License Agreement (&ldquo;EULA&rdquo;) governs the use of all custom
          integration software, automation workflows, synchronization tools, and associated
          services (collectively, the &ldquo;Software&rdquo;) developed and provided by{' '}
          <strong>Phrog AI, LLC dba Bizi Office</strong> (&ldquo;Bizi Office,&rdquo; &ldquo;we,&rdquo;
          &ldquo;us,&rdquo; or &ldquo;our&rdquo;). The Software includes any integration, connector,
          workflow, script, or automation we build for you &mdash; whether connecting QuickBooks
          Online, EspoCRM, n8n, or any other platform, API, or system. By installing, accessing, or
          using the Software, you (&ldquo;You&rdquo; or &ldquo;Licensee&rdquo;) agree to be bound by
          the terms of this EULA. If you do not agree to these terms, do not install, access, or use
          the Software.
        </p>
      </div>

      <nav className="legal-toc" aria-label="Table of contents">
        <h2>Contents</h2>
        <ul>
          <li><a href="#license">License</a></li>
          <li><a href="#restrictions">Restrictions</a></li>
          <li><a href="#disclaimer">Disclaimer</a></li>
          <li><a href="#termination">Termination</a></li>
          <li><a href="#contact">Contact &amp; Changes</a></li>
        </ul>
      </nav>

      <section id="license" className="legal-section">
        <h2>License</h2>
        <p>
          Subject to your compliance with the terms of this EULA, Phrog AI, LLC dba Bizi Office
          grants you a limited, non-exclusive, non-transferable, revocable license to install and
          use the Software solely for your internal business operations. This license applies to all
          custom integrations and automation solutions we build for you, including but not limited to
          integrations with accounting platforms, CRM systems, workflow automation tools, email and
          communication platforms, data processing pipelines, and any other system we connect or
          automate on your behalf. This license includes the following:
        </p>
        <ul className="legal-list">
          <li>
            <strong>Installation &amp; Use:</strong> You may install and use the Software within
            your own environment for the purpose of automating and synchronizing your internal
            systems, regardless of which platforms, APIs, or services the Software integrates with.
          </li>
          <li>
            <strong>Permitted Users:</strong> You may permit your employees, contractors, and
            authorized agents to use the Software on your behalf, provided that they are bound by
            obligations of confidentiality no less restrictive than those in this EULA.
          </li>
          <li>
            <strong>Configuration:</strong> You may configure, customize, and extend the
            Software&rsquo;s workflow logic, field mappings, automation rules, and integration
            parameters to suit your specific business processes and the platforms you use.
          </li>
          <li>
            <strong>Support &amp; Maintenance:</strong> Where included in your service agreement,
            we will provide ongoing support, updates, and maintenance for the duration of your
            engagement. The scope and level of support are defined in your service agreement and
            apply to all custom integrations covered under that agreement.
          </li>
          <li>
            <strong>Ownership:</strong> The Software, including all code, configurations, workflows,
            integration logic, and documentation, remains the intellectual property of Phrog AI,
            LLC unless a separate written agreement transfers ownership. This EULA grants a license
            to use &mdash; not a transfer of ownership &mdash; regardless of which platforms or
            systems the Software integrates with.
          </li>
        </ul>
      </section>

      <section id="restrictions" className="legal-section">
        <h2>Restrictions</h2>
        <p>
          You agree that you will not, and will not permit any third party to, do any of the
          following with respect to the Software or any custom integration we build for you:
        </p>
        <ul className="legal-list">
          <li>
            <strong>Reverse Engineering:</strong> Decompile, disassemble, reverse engineer, or
            otherwise attempt to derive the source code of the Software or any integration logic,
            except as permitted by applicable law.
          </li>
          <li>
            <strong>Redistribution:</strong> Sell, resell, sublicense, rent, lease, distribute, or
            otherwise transfer the Software or any integration, connector, workflow, or derivative
            thereof to any third party, whether for profit or otherwise.
          </li>
          <li>
            <strong>Modification:</strong> Modify, adapt, translate, or create derivative works of
            the Software for redistribution, except for configuring workflows, field mappings,
            automation rules, and integration parameters within your own environment for your own
            use.
          </li>
          <li>
            <strong>Removal of Notices:</strong> Remove, alter, or obscure any copyright, trademark,
            proprietary notice, or attribution within the Software or its associated documentation.
          </li>
          <li>
            <strong>Unauthorized Access:</strong> Use the Software to access or attempt to access
            any system, API, platform, or data without proper authorization, or in a manner that
            violates any applicable law, regulation, or third-party terms of service.
          </li>
          <li>
            <strong>Circumvention:</strong> Circumvent, disable, or otherwise interfere with any
            security, authentication, or access-control mechanism within the Software or any
            connected platform.
          </li>
          <li>
            <strong>Competing Use:</strong> Use the Software to develop, build, or provide a
            competing automation product or service, or to replicate our integration logic for use
            outside the scope of this license, without our prior written consent.
          </li>
          <li>
            <strong>Harmful Use:</strong> Use the Software in any manner that could damage, disable,
            overburden, or impair any connected system, platform, or API, or that could interfere
            with any other party&rsquo;s use and enjoyment of any connected platform.
          </li>
          <li>
            <strong>Scope Violation:</strong> Use the Software to integrate with, access, or
            manipulate systems, APIs, or data outside the scope of the integration as designed and
            delivered by Bizi Office.
          </li>
        </ul>
        <p>
          Violation of any of these restrictions constitutes a material breach of this EULA and may
          result in immediate termination of your license, in addition to any other remedies
          available to us under law or equity.
        </p>
      </section>

      <section id="disclaimer" className="legal-section">
        <h2>Disclaimer</h2>
        <p>
          THE SOFTWARE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE,&rdquo; WITHOUT
          WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT. BIZI OFFICE DOES
          NOT WARRANT THAT THE SOFTWARE OR ANY INTEGRATION WILL BE ERROR-FREE, UNINTERRUPTED, OR
          FREE FROM HARMFUL COMPONENTS.
        </p>
        <ul className="legal-list">
          <li>
            <strong>No Guarantee of Results:</strong> While we design the Software to automate and
            synchronize your internal systems effectively, we do not guarantee specific business
            outcomes, cost savings, or operational results. Your results depend on your
            configuration, data quality, business processes, and the behavior of connected
            platforms.
          </li>
          <li>
            <strong>Third-Party Dependencies:</strong> The Software may depend on third-party
            platforms, APIs, and services (including but not limited to QuickBooks Online, EspoCRM,
            n8n, email providers, CRM systems, accounting platforms, and any other integrated
            service) that are outside our control. We are not responsible for downtime, API changes,
            deprecations, feature removals, rate limits, or disruptions caused by those third-party
            services. Third-party platforms may change their APIs or terms of service at any time,
            which may affect the operation of the Software.
          </li>
          <li>
            <strong>Data Accuracy:</strong> While we take measures to ensure reliable data
            synchronization across all connected systems, you are responsible for verifying the
            accuracy of data in your connected platforms. We recommend periodic audits of
            synchronized records across all integrations.
          </li>
          <li>
            <strong>Integration Compatibility:</strong> We build custom integrations based on the
            platforms and API versions available at the time of development. We are not responsible
            for incompatibilities arising from changes to third-party platforms, APIs, or data
            structures after the integration has been delivered.
          </li>
          <li>
            <strong>Limitation of Liability:</strong> TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE
            LAW, IN NO EVENT SHALL PHROG AI, LLC DBA BIZI OFFICE BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS,
            REVENUE, DATA, OR BUSINESS INTERRUPTION, ARISING OUT OF OR RELATED TO YOUR USE OF THE
            SOFTWARE OR ANY INTEGRATION, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH
            DAMAGES.
          </li>
          <li>
            <strong>Liability Cap:</strong> OUR TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATED
            TO THIS EULA OR THE SOFTWARE SHALL NOT EXCEED THE TOTAL AMOUNTS PAID BY YOU TO BIZI
            OFFICE FOR THE SOFTWARE DURING THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE
            TO THE CLAIM.
          </li>
        </ul>
      </section>

      <section id="termination" className="legal-section">
        <h2>Termination</h2>
        <p>
          This EULA is effective upon your installation or first use of the Software and remains in
          effect until terminated as described below. This EULA applies to all custom integrations
          and automation solutions we build for you, and termination of this EULA applies to all
          such integrations unless a separate agreement specifies otherwise:
        </p>
        <ul className="legal-list">
          <li>
            <strong>Termination by You:</strong> You may terminate this EULA at any time by
            discontinuing use of the Software, disconnecting the relevant integrations, and, where
            applicable, uninstalling the Software from your environment. Upon termination, all
            licenses granted to you under this EULA will immediately cease.
          </li>
          <li>
            <strong>Termination by Us:</strong> We may terminate or suspend this EULA and your
            access to the Software immediately, without prior notice, if you breach any term of
            this EULA, violate any restriction in the Restrictions section, or use the Software in
            a manner that we reasonably believe could cause harm to us, our clients, or any
            third-party platform or service.
          </li>
          <li>
            <strong>Non-Payment:</strong> If the Software is provided under a paid service
            agreement and payment is not received when due, we may suspend or terminate your
            access to the Software after providing reasonable notice and an opportunity to cure.
          </li>
          <li>
            <strong>Effect of Termination:</strong> Upon termination of this EULA for any reason:
            (a) your license to use the Software immediately ends; (b) you must cease all use of
            the Software and all connected integrations; (c) where applicable, you must uninstall
            and delete the Software from your environment; and (d) we may revoke or purge any API
            credentials, tokens, or integration connections associated with your account.
          </li>
          <li>
            <strong>Data After Termination:</strong> Upon termination, we will purge or render
            inaccessible any cached data, credentials, and configuration data associated with your
            integrations, in accordance with our data retention practices. Your underlying data in
            any connected platform &mdash; whether QuickBooks Online, EspoCRM, n8n, or any other
            integrated system &mdash; remains yours and is not affected by the termination of this
            EULA.
          </li>
          <li>
            <strong>Survival:</strong> The sections titled Disclaimer, Termination, and any other
            provisions that by their nature should survive termination will remain in effect after
            termination of this EULA.
          </li>
        </ul>
      </section>

      <section id="contact" className="legal-section">
        <h2>Contact &amp; Changes</h2>
        <p>
          If you have questions about this EULA or your use of the Software, please{' '}
          <Link to="/#contact" className="legal-link">contact us</Link>.
        </p>
        <p>
          We may update this EULA from time to time. The &ldquo;Last updated&rdquo; date at the top
          of this page reflects the most recent revision. Your continued use of the Software after
          any changes constitutes acceptance of the revised terms. We encourage you to review this
          page periodically.
        </p>
        <p>
          This EULA is governed by the laws of the State in which Phrog AI, LLC is registered,
          without regard to its conflict of law provisions.
        </p>
      </section>

      <div className="legal-page-footer">
        <Link to="/" className="btn btn-ghost">&larr; Back to Home</Link>
      </div>
    </div>
  );
}
